import { ChartAvg } from "assets/icons/ChartAvg";
import { ChartDown } from "assets/icons/ChartDown";
import { ChartUp } from "assets/icons/ChartUp";
import ExcelJS from "exceljs";

export const topResultsAmountOptions = [
  { label: "Top 10 CPT Codes", value: 10 },
  { label: "Top 100 CPT Codes", value: 100 },
];

export function exportToCSV(data: any[], filename: string) {
  if (!data.length) return;

  const headers = Object.keys(data[0]);
  const rows = data.map((row) =>
    headers.map((header) => JSON.stringify(row[header] ?? "")).join(",")
  );

  const csvContent = [headers.join(","), ...rows].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function exportToExcel(data: any[], filename: string) {
  if (!data.length) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("FeeSchedule");

  const headers = Object.keys(data[0]);
  worksheet.addRow(headers);
  data.forEach((row) => worksheet.addRow(headers.map((h) => row[h])));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${filename}.xlsx`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const optionsToCompareRates = [
  {
    label: "Highest Avg Commercial Rate",
    value: "maxCommercialAverage",
    icon: ChartUp,
  },
  {
    label: "Lowest Avg Commercial Rate",
    value: "minCommercialAverage",
    icon: ChartDown,
  },
  {
    label: "Medicare Avg Rate",
    value: "medicareAverage",
    icon: ChartAvg,
  },
  {
    label: "Avg Commercial Rate",
    value: "averagePriceOfferedAccrossPayers",
    icon: ChartAvg,
  },
  {
    label: "Market Average Rate",
    value: "marketAverage",
    icon: ChartAvg,
  },
];

export const comparisonRateCode = [
  {
    leftSide: "maxCommercialAverage",
    rightSide: "minCommercialAverage",
    rateToShow: "highestVsLowestCommercial",
  },
  {
    leftSide: "maxCommercialAverage",
    rightSide: "medicareAverage",
    rateToShow: "highestVsMedicare",
  },
  {
    leftSide: "maxCommercialAverage",
    rightSide: "averagePriceOfferedAccrossPayers",
    rateToShow: "highestVsAverage",
  },
  {
    leftSide: "minCommercialAverage",
    rightSide: "maxCommercialAverage",
    rateToShow: "lowestVsHighestCommercial",
  },
  {
    leftSide: "minCommercialAverage",
    rightSide: "medicareAverage",
    rateToShow: "lowestVsMedicare",
  },
  {
    leftSide: "minCommercialAverage",
    rightSide: "averagePriceOfferedAccrossPayers",
    rateToShow: "lowestVsAverage",
  },
  {
    leftSide: "medicareAverage",
    rightSide: "maxCommercialAverage",
    rateToShow: "medicareVsHighest",
  },
  {
    leftSide: "medicareAverage",
    rightSide: "minCommercialAverage",
    rateToShow: "medicareVsLowest",
  },
  {
    leftSide: "medicareAverage",
    rightSide: "averagePriceOfferedAccrossPayers",
    rateToShow: "medicareVsAverage",
  },
  {
    leftSide: "averagePriceOfferedAccrossPayers",
    rightSide: "maxCommercialAverage",
    rateToShow: "averageVsHighest",
  },
  {
    leftSide: "averagePriceOfferedAccrossPayers",
    rightSide: "minCommercialAverage",
    rateToShow: "averageVsLowest",
  },
  {
    leftSide: "averagePriceOfferedAccrossPayers",
    rightSide: "medicareAverage",
    rateToShow: "averageVsMedicare",
  },
  // Market average comparisons
  {
    leftSide: "marketAverage",
    rightSide: "maxCommercialAverage",
    rateToShow: "marketVsHighest",
  },
  {
    leftSide: "marketAverage",
    rightSide: "minCommercialAverage",
    rateToShow: "marketVsLowest",
  },
  {
    leftSide: "marketAverage",
    rightSide: "medicareAverage",
    rateToShow: "marketVsMedicare",
  },
  {
    leftSide: "marketAverage",
    rightSide: "averagePriceOfferedAccrossPayers",
    rateToShow: "marketVsAverage",
  },
  {
    leftSide: "maxCommercialAverage",
    rightSide: "marketAverage",
    rateToShow: "highestVsMarket",
  },
  {
    leftSide: "minCommercialAverage",
    rightSide: "marketAverage",
    rateToShow: "lowestVsMarket",
  },
  {
    leftSide: "medicareAverage",
    rightSide: "marketAverage",
    rateToShow: "medicareVsMarket",
  },
  {
    leftSide: "averagePriceOfferedAccrossPayers",
    rightSide: "marketAverage",
    rateToShow: "averageVsMarket",
  },
];

export const matchModeOptions = [
  { label: "Equals", value: "equals" },
  { label: "Not Equals", value: "notEquals" },
  { label: "Greater Than", value: "gt" },
  { label: "Less Than", value: "lt" },
  { label: "Greater Than or Equal To", value: "gte" },
  { label: "Less Than or Equal To", value: "lte" },
];

// Parsing for the "paste a list of CPT codes" flow on the All Payer Rates
// filter. Users copy code lists out of spreadsheets, contract PDFs and emails,
// so any of comma / semicolon / pipe / whitespace / newline counts as a
// separator rather than insisting on one format.
const CPT_SEPARATOR = /[\s,;|]/;
const CPT_SEPARATORS = /[\s,;|]+/;
// "<codes ending in a separator><partial code>" — lets a half-typed code after
// the last comma stay in the search box instead of being applied.
const CPT_TRAILING_PARTIAL = /^([\s\S]*[\s,;|])([^\s,;|]*)$/;

export function hasCptSeparator(value: string): boolean {
  return CPT_SEPARATOR.test(value);
}

/**
 * Tokenizes a pasted/typed list into normalized (trimmed, upper-cased) codes,
 * dropping blanks and duplicates while preserving the order given.
 */
export function parseCptCodeList(raw: string): string[] {
  const seen = new Set<string>();
  return raw
    .split(CPT_SEPARATORS)
    .map((token) => token.trim().toUpperCase())
    .filter((token) => {
      if (!token || seen.has(token)) return false;
      seen.add(token);
      return true;
    });
}

/**
 * Splits an in-progress filter string into the part that is finished (every
 * code up to and including the last separator) and the code still being typed.
 * With no separator at all the whole string is still in progress.
 */
export function splitCompleteCptCodes(value: string): {
  complete: string;
  partial: string;
} {
  const parts = CPT_TRAILING_PARTIAL.exec(value);
  return parts
    ? { complete: parts[1], partial: parts[2] }
    : { complete: "", partial: value };
}

export function formatCodeList(codes: string[], limit = 10): string {
  const shown = codes.slice(0, limit).join(", ");
  return codes.length > limit
    ? `${shown} (+${codes.length - limit} more)`
    : shown;
}
